import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { HighlightPlate } from '@/components/shared/redesign/HighlightPlate';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { Text } from '@/components/common/text';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { NotificationAvatar } from './mobile/NotificationAvatar';

type Props = {
  item: NotificationItem;
  onPress: () => void;
  isSelected?: boolean;
  showDateSmall?: boolean;
  rightAccessory?: React.ReactNode;
  unread?: boolean;
  dateText?: string;
  compact?: boolean;
  titleStyleOverride?: any;
  rightWidth?: number;
};

export const NotificationRow: React.FC<Props> = ({ item, onPress, isSelected, showDateSmall, rightAccessory, unread, dateText, compact, titleStyleOverride, rightWidth }) => {
  const { colors } = useThemeColors();
  // The whole padded row is the hover target; the press stays on the inner
  // pressable so the right-hand accessory keeps a press of its own. On web,
  // hover and selection share ONE rounded plate pulled space.sm inside the
  // row (the list spans its pane edge to edge): the tint while hovered, the
  // glass fill and blue outline while selected. Off web the selected row
  // keeps its own outline and fill, as before.
  const rowHover = useHoverStyle('none');
  const space = useSpacing();
  const radius = useRadius();
  const isWeb = Platform.OS === 'web';

  const isMarketplaceFeedback = item.notificationType === 'marketplace_feedback';
  const isMarketplaceNotification = item.notificationType === 'marketplace_notification';
  const isMarketplace = isMarketplaceFeedback || isMarketplaceNotification;

  const isPropertyChat = item.notificationType === 'property_chat';

  const listingImageUrl = isMarketplaceFeedback
    ? item.feedbackRequest?.listing?.image
    : isMarketplaceNotification
      ? item.marketplaceNotification?.metadata?.listingImageUrl
      : undefined;

  const avatarVariant = isPropertyChat
    ? 'message'
    : isMarketplace
      ? 'marketplace'
      : 'property';

  return (
    <View
      {...(rowHover.mouseProps as any)}
      style={[
        styles.row,
        compact && styles.rowCompact,
        rowHover.viewStyle,
        isSelected && !isWeb && {
          borderWidth: 1,
          borderColor: PRIMITIVE_COLORS.resioBlue,
          borderRadius: 12,
          backgroundColor: colors.glass.buttonBg,
        },
      ]}
    >
      <HighlightPlate
        visible={isSelected || rowHover.isHovered}
        bleed={-space.sm}
        radius={radius.md}
        color={isSelected ? colors.glass.buttonBg : undefined}
        borderColor={isSelected ? PRIMITIVE_COLORS.resioBlue : undefined}
      />
      <Hoverable hover="none" onPress={onPress} style={styles.rowPress} accessibilityRole="button">
        <View style={styles.rowLeft}>
          <NotificationAvatar
            showUnreadDot={unread}
            variant={avatarVariant}
            imageUrl={listingImageUrl}
            platform="desktop"
          />
        </View>
        <View style={styles.rowTextWrap}>
          {!!showDateSmall && !!dateText && <Text style={[styles.rowDateSmall, { color: colors.text.dim }]}>{dateText}</Text>}
          <Text numberOfLines={2} style={[styles.rowTitle, { color: colors.text.primary }, titleStyleOverride]}>{item.title || item.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
          <Text numberOfLines={2} style={[styles.rowSub, { color: colors.text.subtle }]}>{item.description || item.descriptionHtml || ''}</Text>
        </View>
      </Hoverable>
      {!!rightAccessory && (
        <View style={[styles.rowRight, rightWidth !== undefined ? { width: rightWidth } : null]}>
          {rightAccessory}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 18,
    backgroundColor: 'transparent',
  },
  rowCompact: { paddingVertical: 10 },
  rowPress: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  rowLeft: { paddingRight: 12 },
  rowTextWrap: { flex: 1, paddingRight: 8 },
  rowDateSmall: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
    marginBottom: 4,
  },
  rowTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    fontWeight: '700',
  },
  rowSub: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginTop: 2,
  },
  rowRight: { width: 64, alignItems: 'flex-end' },
});

export default NotificationRow;
